import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  Check,
  Pencil,
  ImagePlus,
  Sparkles,
  X,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { CustomSelect } from '../components/CustomSelect';

const RUBROS = [
  'Moda e Indumentaria',
  'Hogar y Decoración',
  'Belleza y Cuidado Personal',
  'Tecnología',
  'Salud y Bienestar',
  'Automotriz',
  'Gastronomía',
  'Juguetería',
  'Arte y Manualidades',
  'Librería',
  'Creación de Contenido',
  'Otro',
];

// Strip leading title del análisis (ej "## Análisis Estratégico de Marca: X")
const stripLeadingTitle = (text: string): string => {
  const lines = text.split('\n');
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  if (i < lines.length && /^#{1,6}\s+/.test(lines[i])) {
    const content = lines[i].replace(/^#{1,6}\s+/, '').trim();
    if (!/^\d+\s*[\.\)]/.test(content)) {
      i++;
      while (i < lines.length && lines[i].trim() === '') i++;
      return lines.slice(i).join('\n');
    }
  }
  return text;
};

// Simple markdown renderer
const renderMarkdown = (text: string): React.ReactNode => {
  const lines = stripLeadingTitle(text).split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  const parseInline = (line: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let key = 0;
    while (remaining.length > 0) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*|__(.+?)__/);
      if (boldMatch && boldMatch.index !== undefined) {
        if (boldMatch.index > 0) parts.push(remaining.slice(0, boldMatch.index));
        parts.push(<strong key={key++} className="font-semibold">{boldMatch[1] || boldMatch[2]}</strong>);
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
        continue;
      }
      const italicMatch = remaining.match(/\*(.+?)\*|_(.+?)_/);
      if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) parts.push(remaining.slice(0, italicMatch.index));
        parts.push(<em key={key++}>{italicMatch[1] || italicMatch[2]}</em>);
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
        continue;
      }
      parts.push(remaining);
      break;
    }
    return parts.length === 1 ? parts[0] : parts;
  };

  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const ListTag = listType;
      elements.push(
        <ListTag key={elements.length} className={listType === 'ul' ? 'list-disc list-inside my-2' : 'list-decimal list-inside my-2'}>
          {listItems.map((item, i) => <li key={i}>{parseInline(item)}</li>)}
        </ListTag>,
      );
      listItems = [];
      listType = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const text = headerMatch[2];
      if (level <= 2) {
        elements.push(<h3 key={elements.length} className="font-semibold text-brand-black/90 mt-4 mb-1">{parseInline(text)}</h3>);
      } else {
        elements.push(<h4 key={elements.length} className="font-semibold text-brand-black/90 mt-3 mb-1">{parseInline(text)}</h4>);
      }
      continue;
    }
    if (line.match(/^[-*]\s+/)) {
      if (listType !== 'ul') { flushList(); listType = 'ul'; }
      listItems.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }
    if (line.match(/^\d+\.\s+/)) {
      if (listType !== 'ol') { flushList(); listType = 'ol'; }
      listItems.push(line.replace(/^\d+\.\s+/, ''));
      continue;
    }
    if (line.trim() === '') {
      flushList();
      elements.push(<br key={elements.length} />);
      continue;
    }
    flushList();
    elements.push(<p key={elements.length} className="my-1">{parseInline(line)}</p>);
  }
  flushList();
  return elements;
};

type BrandColor = { hex: string; name?: string; role?: string };
type BrandFonts = { suggested?: string[]; style?: string };

interface BrandRow {
  id: string;
  id_company: string;
  name: string;
  rubro: string | null;
  logo_url: string | null;
  user_description: string | null;
  target_public: string | null;
  context: string | null;
  colors: BrandColor[] | null;
  fonts: BrandFonts | null;
  is_default: boolean;
  is_draft: boolean;
}

export const RevisarMarca = () => {
  const navigate = useNavigate();
  const { brandId } = useParams<{ brandId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState<BrandRow | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [rubroOption, setRubroOption] = useState('Moda e Indumentaria');
  const [customRubro, setCustomRubro] = useState('');
  const [context, setContext] = useState('');
  const [colors, setColors] = useState<BrandColor[]>([]);
  const [fonts, setFonts] = useState<BrandFonts>({ suggested: [], style: '' });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Edit modes
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false);
  const [analysisDraft, setAnalysisDraft] = useState('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Cargar brand al montar
  useEffect(() => {
    if (!brandId) {
      setError('No se encontró la marca a revisar');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: dbError } = await supabase
          .from('brand_profiles')
          .select('*')
          .eq('id', brandId)
          .single();
        if (dbError) throw dbError;
        if (cancelled) return;
        const b = data as BrandRow;
        setBrand(b);
        setName(b.name || '');
        const rubroVal = b.rubro || 'Moda e Indumentaria';
        if (RUBROS.includes(rubroVal)) {
          setRubroOption(rubroVal);
          setCustomRubro('');
        } else if (rubroVal.trim().length > 0) {
          setRubroOption('Otro');
          setCustomRubro(rubroVal);
        }
        setContext(b.context || '');
        setColors(Array.isArray(b.colors) ? b.colors : []);
        setFonts(b.fonts || { suggested: [], style: '' });
        setLogoUrl(b.logo_url);
      } catch (e: any) {
        console.error('Error loading brand to review:', e);
        if (!cancelled) setError('No se pudo cargar la marca');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId]);

  // Google Fonts dinámico
  useEffect(() => {
    const suggested = fonts?.suggested;
    if (!Array.isArray(suggested) || suggested.length === 0) return;
    const families = suggested
      .map((f) => f.trim().replace(/\s+/g, '+'))
      .filter(Boolean)
      .map((f) => `family=${f}:wght@400;500;600`)
      .join('&');
    if (!families) return;
    const id = `revisar-marca-fonts-${brandId}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }, [fonts, brandId]);

  const effectiveRubro = rubroOption === 'Otro' ? customRubro.trim() : rubroOption;

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!brand || !e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    if (!file.type.startsWith('image/')) {
      setError('El archivo debe ser una imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede pesar más de 5MB');
      return;
    }
    try {
      setUploadingLogo(true);
      setError(null);
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}.${fileExt}`;
      const filePath = `logos/${fileName}`;
      const { error: upErr } = await supabase.storage.from('system').upload(filePath, file);
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from('system').getPublicUrl(filePath);
      setLogoUrl(publicUrl);
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError('No se pudo subir el logo');
    } finally {
      setUploadingLogo(false);
      (e.target as HTMLInputElement).value = '';
    }
  };

  const handleColorChange = (idx: number, newHex: string) => {
    setColors((prev) => prev.map((c, i) => (i === idx ? { ...c, hex: newHex } : c)));
  };

  const openAnalysisEditor = () => {
    setAnalysisDraft(context);
    setIsEditingAnalysis(true);
  };
  const cancelAnalysisEdit = () => {
    setAnalysisDraft('');
    setIsEditingAnalysis(false);
  };
  const saveAnalysisEdit = () => {
    setContext(analysisDraft);
    setIsEditingAnalysis(false);
  };

  const handleConfirm = async () => {
    if (!brand) return;
    if (!name.trim()) {
      setError('El nombre de la marca no puede estar vacío');
      return;
    }
    if (!effectiveRubro) {
      setError('Tenés que elegir un rubro');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload: Record<string, any> = {
        name: name.trim(),
        rubro: effectiveRubro,
        context,
        colors,
        fonts,
        logo_url: logoUrl,
        is_draft: false,
      };
      const { error: updErr } = await supabase
        .from('brand_profiles')
        .update(payload)
        .eq('id', brand.id);
      if (updErr) throw updErr;

      // Mirror al company si es default (para sidebar/legacy)
      if (brand.is_default) {
        const updates: Record<string, any> = {
          nombre: name.trim(),
          rubro: effectiveRubro,
        };
        if (logoUrl) updates.logo_url = logoUrl;
        await supabase
          .from('company_profiles')
          .update(updates)
          .eq('id_company', brand.id_company);
      }

      window.dispatchEvent(
        new CustomEvent('atlas:brand-changed', { detail: { brandId: brand.id } }),
      );

      navigate('/redes-sociales/crear-anuncio/seleccionar-productos', {
        replace: true,
        state: { hideBrandBanner: true },
      });
    } catch (err: any) {
      console.error('Error confirming brand:', err);
      setError('No se pudo guardar. Intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#F2F2F7]">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (error && !brand) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#F2F2F7] px-4">
        <div className="bg-surface-card rounded-lg border border-neutral-200 p-6 max-w-md w-full">
          <AlertCircle className="w-6 h-6 text-red-500 mb-3" />
          <p className="text-[15px] text-brand-black/80">{error}</p>
          <button
            onClick={() => navigate('/redes-sociales/crear-anuncio/seleccionar-productos')}
            className="mt-4 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md text-[14px] font-medium"
          >
            Ir al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[#F2F2F7] pb-32 sm:pb-28">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-neutral-200 px-4 sm:px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-brand-primary mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="text-[12px] font-medium uppercase tracking-wide">
              Análisis listo
            </span>
          </div>
          <h1 className="text-[20px] sm:text-[24px] font-semibold text-brand-black/90">
            Revisá tu marca
          </h1>
          <p className="text-[13px] sm:text-[14px] text-neutral-500 mt-1">
            La IA generó esta identidad a partir de lo que nos contaste. Ajustá lo
            que quieras antes de empezar — después también vas a poder modificarlo
            desde Mis marcas.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-4 sm:space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <p className="text-[13px] text-red-700">{error}</p>
          </div>
        )}

        {/* Card: Logo + Nombre + Rubro */}
        <div className="bg-surface-card rounded-lg border border-neutral-200 p-4">
          <div className="flex items-start gap-4">
            <div className="shrink-0 flex flex-col items-center gap-2">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-neutral-200 bg-white">
                <img
                  src={logoUrl || 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg'}
                  alt="Logo"
                  className="w-full h-full object-contain p-1"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//Logo.svg';
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="h-7 px-2.5 rounded-md bg-surface-card border border-neutral-200 text-[11px] font-medium text-brand-black/90 hover:bg-neutral-50 shadow-sm transition-colors inline-flex items-center gap-1"
              >
                {uploadingLogo ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <ImagePlus className="w-3 h-3" />
                    Cambiar
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                disabled={uploadingLogo}
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-1.5">
                Nombre de la marca
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre de la marca"
                className="w-full h-10 px-3 border border-neutral-200 rounded-md text-[16px] sm:text-[18px] font-semibold text-brand-black/90 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors mb-3"
              />
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-1.5">
                Rubro
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <CustomSelect
                  options={RUBROS.map((r) => ({ value: r, label: r }))}
                  value={rubroOption}
                  onChange={(v) => {
                    setRubroOption(v);
                    if (v !== 'Otro') setCustomRubro('');
                  }}
                  className="min-w-[180px] shrink-0"
                />
                {rubroOption === 'Otro' && (
                  <input
                    type="text"
                    value={customRubro}
                    onChange={(e) => setCustomRubro(e.target.value)}
                    placeholder="Especificá tu rubro"
                    className="flex-1 min-w-[160px] h-9 px-3 rounded-md bg-white border border-neutral-200 text-[14px] text-brand-black/90 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Card: Tipografía */}
        {fonts && Array.isArray(fonts.suggested) && fonts.suggested.length > 0 && (
          <div className="bg-surface-card rounded-lg border border-neutral-200 p-4">
            <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
              Tipografía
            </div>
            <div className="grid grid-cols-2 gap-3">
              {fonts.suggested.slice(0, 2).map((font, idx) => (
                <div key={`${font}-${idx}`} className="flex flex-col items-center text-center">
                  <div
                    className="text-[40px] sm:text-[48px] leading-none text-brand-black/90 mb-1"
                    style={{ fontFamily: `"${font}", serif` }}
                  >
                    Aa
                  </div>
                  <div
                    className="text-[13px] text-brand-black/80 truncate w-full px-1"
                    style={{ fontFamily: `"${font}", serif` }}
                  >
                    {font}
                  </div>
                  <div className="text-[10px] text-neutral-500 uppercase tracking-wide mt-1">
                    {idx === 0 ? 'Principal' : 'Secundaria'}
                  </div>
                </div>
              ))}
            </div>
            {fonts.style && (
              <p className="text-[11px] text-neutral-500 italic pt-3 mt-3 border-t border-neutral-200/70">
                {fonts.style}
              </p>
            )}
          </div>
        )}

        {/* Card: Paleta de colores — editables con pencil overlay */}
        {colors.length > 0 && (
          <div className="bg-surface-card rounded-lg border border-neutral-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
                Paleta de colores
              </div>
              <span className="text-[11px] text-neutral-400 inline-flex items-center gap-1">
                <Pencil className="w-3 h-3" />
                Tocá un color para cambiarlo
              </span>
            </div>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              {colors.slice(0, 6).map((c, idx) => (
                <div key={idx} className="flex flex-col items-center gap-1.5">
                  <label
                    className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-neutral-200 shadow-sm cursor-pointer block group transition-transform hover:scale-105"
                    style={{ backgroundColor: c.hex }}
                    title={`${c.name || ''} · ${c.hex}`}
                  >
                    {/* Pencil overlay — solo aparece en hover */}
                    <span className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <Pencil className="w-4 h-4 text-white" />
                    </span>
                    {/* Uncontrolled: usamos defaultValue para que el picker mantenga su estado interno
                        mientras el usuario lo arrastra. onChange solo dispara al cerrar/confirmar. */}
                    <input
                      type="color"
                      defaultValue={c.hex}
                      onChange={(e) => handleColorChange(idx, e.target.value)}
                      className="opacity-0 absolute inset-0 cursor-pointer rounded-full"
                    />
                  </label>
                  <div className="text-[9px] sm:text-[10px] text-neutral-500 font-mono">
                    {c.hex}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Card: Análisis — view mode + pencil to edit */}
        <div className="bg-surface-card rounded-lg border border-neutral-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider">
              Análisis de marca
            </div>
            {!isEditingAnalysis ? (
              <button
                type="button"
                onClick={openAnalysisEditor}
                className="h-7 px-2 rounded-md bg-surface-card border border-neutral-200 text-[11px] font-medium text-brand-black/80 hover:bg-neutral-50 inline-flex items-center gap-1 transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Editar
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={cancelAnalysisEdit}
                  className="h-7 px-2 rounded-md bg-surface-card border border-neutral-200 text-[11px] font-medium text-brand-black/80 hover:bg-neutral-50 inline-flex items-center gap-1 transition-colors"
                >
                  <X className="w-3 h-3" />
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={saveAnalysisEdit}
                  className="h-7 px-2.5 rounded-md bg-brand-primary text-white text-[11px] font-medium hover:bg-brand-primary-hover inline-flex items-center gap-1 transition-colors"
                >
                  <Check className="w-3 h-3" />
                  Listo
                </button>
              </div>
            )}
          </div>

          {!isEditingAnalysis ? (
            context ? (
              <div className="text-[13px] text-brand-black/80 leading-relaxed">
                {renderMarkdown(context)}
              </div>
            ) : (
              <p className="text-[13px] text-neutral-500 italic">
                Todavía no hay análisis. Tocá Editar para escribir uno.
              </p>
            )
          ) : (
            <textarea
              value={analysisDraft}
              onChange={(e) => setAnalysisDraft(e.target.value)}
              rows={16}
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-[13px] text-brand-black/80 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-y leading-relaxed"
              autoFocus
            />
          )}
        </div>
      </div>

      {/* Sticky footer */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 px-4 sm:px-6 py-3 sm:py-4 z-20"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      >
        <div className="max-w-3xl mx-auto flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || uploadingLogo || isEditingAnalysis}
            title={isEditingAnalysis ? 'Terminá de editar el análisis antes de confirmar' : ''}
            className="w-full sm:w-auto h-11 px-6 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md text-[15px] font-semibold shadow-sm transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Confirmar y empezar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisarMarca;
